import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Divider, Form, Input, Modal, Popconfirm, Space, Table, Tag, message, Select, Tabs, Tooltip, Switch, Typography, Upload } from "antd";
import { FaChartLine, FaFire, FaPen, FaTrash, FaUndo, FaGripVertical, FaFileAlt, FaDownload, FaUpload, FaExclamationTriangle, FaTimesCircle, FaCheckCircle, FaSync } from "react-icons/fa";
import { TagOutlined } from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import axiosHttp from "../../appRedux/services/axiosCommon";
import Page from "../../components/Common/Page";

const renderTagIcon = (tagObj) => {
  if (!tagObj) return <TagOutlined style={{ fontSize: 12, color: '#1890ff', marginRight: 4 }} />;
  const iconStr = tagObj.icon;
  if (iconStr && (iconStr.startsWith("http://") || iconStr.startsWith("https://") || iconStr.startsWith("/"))) {
    return (
      <img
        src={iconStr}
        alt=""
        style={{ width: 14, height: 14, objectFit: "contain", verticalAlign: "middle", borderRadius: 2, marginRight: 4 }}
      />
    );
  }
  if (iconStr && iconStr.trim()) {
    return <span style={{ fontSize: 13, lineHeight: 1, marginRight: 4 }}>{iconStr.trim()}</span>;
  }
  return <TagOutlined style={{ fontSize: 12, color: '#1890ff', marginRight: 4 }} />;
};

const buildSkillTree = (flatList) => {
  const map = {};
  const roots = [];

  // Clone nodes and prepare children arrays
  flatList.forEach((node) => {
    map[node._id] = { ...node, children: [] };
  });

  // Attach children to parents or place at root
  flatList.forEach((node) => {
    if (node.parent && node.parent._id && map[node.parent._id]) {
      map[node.parent._id].children.push(map[node._id]);
    } else {
      roots.push(map[node._id]);
    }
  });

  // Remove empty children arrays to prevent false expand icons in Table
  Object.values(map).forEach((node) => {
    if (node.children.length === 0) delete node.children;
  });

  return roots;
};

const filterSkillTree = (nodes, checkMatchFn, parentMatches = false) => {
  return nodes
    .map((node) => {
      const isSelfMatch = checkMatchFn(node);
      const isMatched = isSelfMatch || parentMatches;

      const filteredChildren = node.children
        ? filterSkillTree(node.children, checkMatchFn, isMatched)
        : undefined;

      const hasMatchingChildren = filteredChildren && filteredChildren.length > 0;

      if (isMatched || hasMatchingChildren) {
        return {
          ...node,
          children: filteredChildren && filteredChildren.length > 0 ? filteredChildren : undefined,
        };
      }
      return null;
    })
    .filter(Boolean);
};


const PAGE_STATUS_COLORS = {
  missing: "default",
  draft: "gold",
  published: "green",
  archived: "red",
};

const PAGE_STATUS_LABELS = {
  missing: "Missing",
  draft: "Draft",
  published: "Published",
  archived: "Archived",
};

const PAGE_STATUS_DESCRIPTIONS = {
  missing: "No editable page document exists yet.",
  draft: "Editable internally; not served by public API.",
  published: "Live on public skill pages.",
  archived: "Kept for recovery; hidden from public API.",
};

const STAGE_LEVEL_LABELS = {
  beginner: "Beginner",
  intermediate: "Intermediate",
  advanced: "Advanced",
};



const getApiErrorMessage = (error, fallback) => (
  error.response?.data?.error?.message ||
  error.response?.data?.message ||
  error.message ||
  fallback
);

const StringList = ({ name, addLabel, placeholder }) => (
  <Form.List name={name}>
    {(fields, { add, remove }) => (
      <div>
        {fields.map((field) => (
          <Space key={field.key} align="baseline" style={{ display: "flex", marginBottom: 8 }}>
            <Form.Item {...field} style={{ flex: 1, marginBottom: 0 }}>
              <Input placeholder={placeholder} />
            </Form.Item>
            <Button danger type="text" onClick={() => remove(field.name)}>Remove</Button>
          </Space>
        ))}
        <Button type="dashed" onClick={() => add()} block>{addLabel}</Button>
      </div>
    )}
  </Form.List>
);

const IconAction = ({ title, color, icon, onClick, danger = false, disabled = false }) => (
  <Tooltip title={title}>
    <Button
      type="text"
      size="small"
      shape="circle"
      danger={danger}
      disabled={disabled}
      icon={icon}
      onClick={onClick}
      style={{
        color: disabled || danger ? undefined : color,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    />
  </Tooltip>
);

const StatusPill = ({ status }) => (
  <Tag color={PAGE_STATUS_COLORS[status] || "default"} style={{ marginRight: 0, fontWeight: 700 }}>
    {PAGE_STATUS_LABELS[status] || status}
  </Tag>
);

const StatCard = ({ label, value, tone }) => (
  <Card size="small" style={{ borderRadius: 8, borderColor: "#edf1f7", height: "100%" }}>
    <div style={{ fontSize: 12, color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>{label}</div>
    <div style={{ fontSize: 24, fontWeight: 800, color: tone || "#0f172a", lineHeight: 1.2 }}>{value}</div>
  </Card>
);

const SkillList = () => {
  // --- Skills State ---
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSkill, setEditingSkill] = useState(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState("");
  const [dragOverSkillId, setDragOverSkillId] = useState(null);
  const [recentlyUpdatedCodes, setRecentlyUpdatedCodes] = useState(new Set());
  const [showRecentlyUpdatedOnly, setShowRecentlyUpdatedOnly] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  // --- Topics State ---
  const [topics, setTopics] = useState([]);
  const [categories, setCategories] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [isTopicModalOpen, setIsTopicModalOpen] = useState(false);
  const [editingTopic, setEditingTopic] = useState(null);
  const [savingTopic, setSavingTopic] = useState(false);
  const [topicForm] = Form.useForm();
  const [topicSearchText, setTopicSearchText] = useState("");
  const [topicStatusFilter, setTopicStatusFilter] = useState("all");
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(tabFromUrl || "skills");
  const navigate = useNavigate();

  const handleTabChange = (key) => {
    setActiveTab(key);
    setSearchParams({ tab: key }, { replace: true });
  };

  // --- Skill CSV Export & Import States ---
  const [exporting, setExporting] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importFileList, setImportFileList] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importPreviewData, setImportPreviewData] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const fetchSkills = async () => {
    setLoading(true);
    try {
      const response = await axiosHttp.get("/v1/skill");
      if (response.data?.status) {
        setSkills(response.data.results || []);
      } else {
        message.error(response.data?.message || "Failed to fetch skills");
      }
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to fetch skills");
    } finally {
      setLoading(false);
    }
  };

  const fetchTopics = async () => {
    setLoadingTopics(true);
    try {
      const response = await axiosHttp.get("/v3/admin/topics");
      if (response.data?.data || response.data?.results || Array.isArray(response.data)) {
        setTopics(response.data.data || response.data.results || response.data || []);
      } else {
        message.error("Failed to fetch topics");
      }
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to fetch topics");
    } finally {
      setLoadingTopics(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axiosHttp.get("/v1/category");
      if (response.data?.status) {
        setCategories(response.data.results || []);
      }
    } catch (error) {
      console.error("Failed to fetch categories", error);
    }
  };

  const fetchAvailableTags = async () => {
    try {
      const response = await axiosHttp.get("/v3/tags?scope=skill");
      if (response.data?.data) {
        setAvailableTags(response.data.data);
      } else if (Array.isArray(response.data)) {
        setAvailableTags(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch available tags", error);
    }
  };

  useEffect(() => {
    fetchSkills();
    fetchCategories();
    fetchTopics();
    fetchAvailableTags();
  }, []);

  // --- Skill Methods ---
  const openCreateModal = () => {
    setEditingSkill(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const openEditModal = (skill) => {
    setEditingSkill(skill);
    form.setFieldsValue({
      name: skill.name,
      code: skill.code,
      slug: skill.slug,
      parent: skill.parent ? skill.parent._id : null,
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const payload = { ...values, parent: values.parent || null };

      if (editingSkill?._id) {
        const response = await axiosHttp.put("/v1/skill", {
          id: editingSkill._id,
          ...payload,
        });
        if (!response.data?.status) throw new Error(response.data?.message || "Update failed");
        message.success("Skill updated successfully.");
      } else {
        const response = await axiosHttp.post("/v1/skill", payload);
        if (!response.data?.status) throw new Error(response.data?.message || "Create failed");
        message.success("Skill created successfully.");
      }

      setIsModalOpen(false);
      fetchSkills();
      if (activeTab === "topics") fetchTopics();
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error.message || error.response?.data?.message || "Failed to save skill");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (skill) => {
    try {
      const response = await axiosHttp.delete("/v1/skill", {
        data: { id: skill._id },
      });
      if (!response.data?.status) throw new Error(response.data?.message || "Delete failed");
      message.success("Skill deleted successfully.");
      fetchSkills();
      if (activeTab === "topics") fetchTopics();
    } catch (error) {
      message.error(error.message || error.response?.data?.message || "Failed to delete skill");
    }
  };

  const handleDragDropParentChange = async (draggedId, targetParentId) => {
    const draggedSkill = skills.find(s => s._id === draggedId);
    const targetParent = skills.find(s => s._id === targetParentId);
    if (!draggedSkill || !targetParent) return;

    const isAncestor = (ancestorId, childId) => {
      let current = skills.find(s => s._id === childId);
      while (current && current.parent) {
        const pId = current.parent._id || current.parent;
        if (pId === ancestorId) return true;
        current = skills.find(s => s._id === pId);
      }
      return false;
    };

    if (draggedId === targetParentId) {
      message.error("A skill cannot be its own parent.");
      return;
    }

    if (isAncestor(draggedId, targetParentId)) {
      message.error("Cyclic relationship detected. Cannot place a parent skill under its own child.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: draggedSkill.name,
        code: draggedSkill.code,
        slug: draggedSkill.slug,
        parent: targetParentId,
      };

      const response = await axiosHttp.put("/v1/skill", {
        id: draggedId,
        ...payload,
      });

      if (!response.data?.status) throw new Error(response.data?.message || "Reparenting failed");
      
      // Update local state directly to guarantee immediate refresh
      setSkills(prevSkills => {
        return prevSkills.map(s => {
          if (s._id === draggedId) {
            return {
              ...s,
              parent: {
                _id: targetParentId,
                name: targetParent.name
              }
            };
          }
          return s;
        });
      });

      message.success(`Reparented "${draggedSkill.name}" under "${targetParent.name}".`);
      fetchSkills();
    } catch (error) {
      message.error(error.message || "Failed to update skill parent relationship.");
    } finally {
      setLoading(false);
    }
  };

  // --- Topic Methods ---
  const openCreateTopicModal = () => {
    setEditingTopic(null);
    topicForm.resetFields();
    topicForm.setFieldsValue({ is_trending: false, is_popular: false, description: '', custom_tags: [] });
    setIsTopicModalOpen(true);
  };

  const openCreateTopicModalWithSkill = (skillId) => {
    setEditingTopic(null);
    topicForm.resetFields();
    topicForm.setFieldsValue({ 
      skill_id: skillId,
      is_trending: false,
      is_popular: false,
      description: '',
      custom_tags: []
    });
    setIsTopicModalOpen(true);
  };

  const openEditTopicModal = (topic) => {
    setEditingTopic(topic);
    const existingTags = topic.tags || [];
    
    const trendingTag = availableTags.find(t => t.slug === 'trending');
    const popularTag = availableTags.find(t => t.slug === 'popular');

    const isTrending = existingTags.some(t => t === trendingTag?._id || t._id === trendingTag?._id);
    const isPopular = existingTags.some(t => t === popularTag?._id || t._id === popularTag?._id);

    const customTags = existingTags.map(t => t._id || t).filter(id => {
      return id !== trendingTag?._id && id !== popularTag?._id;
    });

    topicForm.setFieldsValue({
      skill_id: topic._id,
      categories: topic.categories ? topic.categories.map(c => c._id || c) : [],
      is_trending: isTrending,
      is_popular: isPopular,
      description: topic.description || '',
      custom_tags: customTags
    });
    setIsTopicModalOpen(true);
  };

  // Helper: build the tags array by merging trait keywords with any existing non-trait tags
  const buildTraitTags = (existingTags = [], isTrending, isPopular) => {
    const trendingTag = availableTags.find(t => t.slug === 'trending');
    const popularTag = availableTags.find(t => t.slug === 'popular');
    
    // Keep all non-trait tags (extract _id if object)
    const nonTraitTags = existingTags.map(t => t._id || t).filter(id => {
      return id !== trendingTag?._id && id !== popularTag?._id;
    });

    const traitTags = [
      ...(isTrending && trendingTag ? [trendingTag._id] : []),
      ...(isPopular && popularTag ? [popularTag._id] : []),
    ];
    return [...nonTraitTags, ...traitTags];
  };

  const ensureTopicTagsCreated = async (rawTags) => {
    if (!Array.isArray(rawTags)) return [];
    const processedIds = [];
    let updatedList = [...availableTags];
    let listChanged = false;

    for (const item of rawTags) {
      if (!item) continue;
      const existing = updatedList.find(
        t => String(t._id) === String(item) || t.name?.toLowerCase() === String(item).toLowerCase()
      );

      if (existing) {
        processedIds.push(existing._id);
      } else {
        try {
          const createRes = await axiosHttp.post("/v3/admin/tags", {
            name: String(item).trim(),
            scope: "skill"
          });
          const newTag = createRes.data?.data;
          if (newTag && newTag._id) {
            processedIds.push(newTag._id);
            updatedList.push(newTag);
            listChanged = true;
          }
        } catch (err) {
          console.error("Failed to create new tag:", item, err);
        }
      }
    }

    if (listChanged) {
      setAvailableTags(updatedList);
    }
    return processedIds;
  };

  const handleTopicSubmit = async () => {
    try {
      const values = await topicForm.validateFields();
      setSavingTopic(true);

      const customTags = values.custom_tags || [];
      if (customTags.length > 3) {
        message.warning("You can select a maximum of 3 tags for a topic.");
        setSavingTopic(false);
        return;
      }

      const processedTags = await ensureTopicTagsCreated(customTags);
      const newTags = buildTraitTags(processedTags, values.is_trending, values.is_popular);

      const payload = {
        tags: newTags,
        categories: values.categories || [],
        description: values.description || ''
      };

      const response = await axiosHttp.put(`/v3/admin/topics/${values.skill_id}`, payload);
      if (!response.data?.data) throw new Error(response.data?.message || "Operation failed");
      
      message.success("Topic saved successfully.");
      setIsTopicModalOpen(false);
      fetchTopics();
      if (activeTab === "skills") fetchSkills();
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error.message || error.response?.data?.message || "Failed to save topic");
    } finally {
      setSavingTopic(false);
    }
  };

  const handleRemoveTopic = async (topic) => {
    try {
      const response = await axiosHttp.delete(`/v3/admin/topics/${topic._id}`);
      if (!response.data?.data) throw new Error(response.data?.message || "Failed to remove topic");
      
      message.success("Topic removed successfully.");
      fetchTopics();
      if (activeTab === "skills") fetchSkills();
    } catch (error) {
      message.error(error.message || error.response?.data?.message || "Failed to remove topic");
    }
  };

  const toggleTrending = async (topic) => {
    try {
      const currentTags = topic.tags || [];
      const trendingTag = availableTags.find(t => t.slug === 'trending');
      const popularTag = availableTags.find(t => t.slug === 'popular');
      
      const isTrending = currentTags.some(t => t === trendingTag?._id || t._id === trendingTag?._id);
      const isPopular = currentTags.some(t => t === popularTag?._id || t._id === popularTag?._id);
      
      const newTags = buildTraitTags(currentTags, !isTrending, isPopular);
      const payload = { tags: newTags, categories: (topic.categories || []).map((c) => c._id || c) };
      const response = await axiosHttp.put(`/v3/admin/topics/${topic._id}`, payload);
      if (!response.data?.data) throw new Error(response.data?.message || "Failed to update trending status");
      
      message.success(`Topic ${!isTrending ? "marked as trending" : "removed from trending"}.`);
      fetchTopics();
    } catch (error) {
      message.error(error.message || error.response?.data?.message || "Failed to update trending status");
    }
  };

  const togglePopular = async (topic) => {
    try {
      const currentTags = topic.tags || [];
      const trendingTag = availableTags.find(t => t.slug === 'trending');
      const popularTag = availableTags.find(t => t.slug === 'popular');
      
      const isTrending = currentTags.some(t => t === trendingTag?._id || t._id === trendingTag?._id);
      const isPopular = currentTags.some(t => t === popularTag?._id || t._id === popularTag?._id);
      
      const newTags = buildTraitTags(currentTags, isTrending, !isPopular);
      const payload = { tags: newTags, categories: (topic.categories || []).map((c) => c._id || c) };
      const response = await axiosHttp.put(`/v3/admin/topics/${topic._id}`, payload);
      if (!response.data?.data) throw new Error(response.data?.message || "Failed to update popularity status");
      
      message.success(`Topic ${!isPopular ? "marked as popular" : "removed from popular"}.`);
      fetchTopics();
    } catch (error) {
      message.error(error.message || error.response?.data?.message || "Failed to update popularity status");
    }
  };

  const createOrRepairPage = async (topic) => {
    try {
      const response = await axiosHttp.post(`/v3/admin/topics/${topic._id}/page`);
      const newPage = response.data?.data;
      if (!newPage?._id) throw new Error("Failed to create skill page");
      message.success("Draft skill page initialized. Redirecting...");
      navigate(`/skill/builder/${newPage._id}`);
    } catch (error) {
      message.error(getApiErrorMessage(error, "Failed to create skill page"));
    }
  };

  const openPageEditor = (topic) => {
    if (!topic.page?._id) {
      createOrRepairPage(topic);
      return;
    }
    navigate(`/skill/builder/${topic.page._id}`);
  };

  // --- Computations ---
  const skillTableData = useMemo(() => {
    let tree = buildSkillTree(skills);

    // Apply recently updated filter
    if (showRecentlyUpdatedOnly && recentlyUpdatedCodes.size > 0) {
      tree = filterSkillTree(tree, (node) => recentlyUpdatedCodes.has(node.code));
    }

    // Apply search query filter
    const query = searchText.trim().toLowerCase();
    if (query) {
      tree = filterSkillTree(tree, (node) =>
        String(node.name || "").toLowerCase().includes(query) ||
        String(node.code || "").toLowerCase().includes(query) ||
        String(node.slug || "").toLowerCase().includes(query)
      );
    }

    return tree;
  }, [skills, searchText, showRecentlyUpdatedOnly, recentlyUpdatedCodes]);

  useEffect(() => {
    const isSearching = searchText.trim() !== "";
    const isFilterActive = showRecentlyUpdatedOnly && recentlyUpdatedCodes.size > 0;

    if (isSearching || isFilterActive) {
      const keys = [];
      const collectKeys = (nodes) => {
        nodes.forEach((node) => {
          if (node.children && node.children.length > 0) {
            keys.push(node._id);
            collectKeys(node.children);
          }
        });
      };
      collectKeys(skillTableData);
      setExpandedRowKeys(keys);
    } else {
      setExpandedRowKeys([]);
    }
  }, [searchText, showRecentlyUpdatedOnly, recentlyUpdatedCodes, skillTableData]);

  const getTopicPageStatusWeight = (topic) => {
    const status = (topic.page?.status || "missing").toLowerCase();
    if (status === "published" || status === "active") return 1;
    if (status === "draft") return 2;
    if (status === "archived" || status === "inactive") return 3;
    return 4; // missing / uninitialized
  };

  const topicTableData = useMemo(() => {
    let result = [...topics];

    if (topicStatusFilter === "initialized") {
      result = result.filter((t) => {
        const s = (t.page?.status || "missing").toLowerCase();
        return s !== "missing";
      });
    } else if (topicStatusFilter === "missing") {
      result = result.filter((t) => {
        const s = (t.page?.status || "missing").toLowerCase();
        return s === "missing";
      });
    }

    const query = topicSearchText.trim().toLowerCase();
    if (query) {
      result = result.filter(
        (topic) =>
          String(topic.name || "").toLowerCase().includes(query) ||
          String(topic.code || "").toLowerCase().includes(query)
      );
    }

    result.sort((a, b) => {
      const weightA = getTopicPageStatusWeight(a);
      const weightB = getTopicPageStatusWeight(b);

      if (weightA !== weightB) {
        return weightA - weightB;
      }

      return String(a.name || "").localeCompare(String(b.name || ""));
    });

    return result;
  }, [topics, topicSearchText, topicStatusFilter]);

  const parentOptions = useMemo(() => {
    return skills
      .filter((s) => !editingSkill || s._id !== editingSkill._id)
      .map((s) => ({ value: s._id, label: s.name }));
  }, [skills, editingSkill]);

  const allSkillsOptions = useMemo(() => {
    return skills.map((s) => ({ value: s._id, label: s.name }));
  }, [skills]);

  const categoryOptions = useMemo(() => {
    return categories.map((c) => ({ value: c._id, label: c.name || c.title || c.code }));
  }, [categories]);

  const topicStats = useMemo(() => {
    const counts = topics.reduce(
      (acc, topic) => {
        const status = (topic.page?.status || "missing").toLowerCase();
        acc.total += 1;
        if (status !== "missing") {
          acc.initialized = (acc.initialized || 0) + 1;
        }
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      },
      { total: 0, initialized: 0, missing: 0, draft: 0, published: 0, archived: 0 }
    );
    return counts;
  }, [topics]);

  // --- Columns ---
  const skillColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 500,
      render: (value, record) => {
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              className="drag-handle"
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData("draggedSkillId", record._id);
                e.dataTransfer.effectAllowed = "move";
              }}
              style={{
                cursor: "grab",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 24,
                height: 24,
                borderRadius: "4px",
                color: "#94a3b8",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#f5f5f5";
                e.currentTarget.style.color = "#1890ff";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
                e.currentTarget.style.color = "#94a3b8";
              }}
            >
              <FaGripVertical style={{ fontSize: "14px" }} />
            </span>
            <div style={{ flex: 1 }}>
              <strong style={{ fontSize: '15px' }}>{value}</strong>
            </div>
          </div>
        );
      },
    },
    {
      title: "Slug",
      dataIndex: "slug",
      key: "slug",
      width: 240,
      render: (value) => (
        value ? (
          <code style={{ background: '#f1f5f9', padding: '3px 8px', borderRadius: '4px', color: '#475569', fontSize: '12px', fontFamily: 'SFMono-Regular, Consolas, Monaco, monospace' }}>
            /{value}
          </code>
        ) : (
          <span style={{ color: '#bfbfbf', fontStyle: 'italic' }}>--</span>
        )
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      width: 220,
      render: (_, record) => {
        const existingTopic = topics.find((t) => t._id === record._id);
        return (
          <div className="d-flex align-items-center justify-content-center gap-3">
            {existingTopic ? (
              <Tooltip title="Configure Topic Settings">
                <span
                  onClick={() => openEditTopicModal(existingTopic)}
                  style={{
                    padding: "3px 8px",
                    borderRadius: "4px",
                    fontSize: "11px",
                    fontWeight: "600",
                    backgroundColor: "#e6f7ff",
                    color: "#1890ff",
                    border: "1px solid #91d5ff",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    transition: "all 0.2s",
                    height: "24px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#bae7ff";
                    e.currentTarget.style.borderColor = "#69c0ff";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "#e6f7ff";
                    e.currentTarget.style.borderColor = "#91d5ff";
                  }}
                >
                  <span style={{ display: "inline-block", width: "6px", height: "6px", borderRadius: "50%", backgroundColor: "#1890ff" }} />
                  Topic Active
                </span>
              </Tooltip>
            ) : (
              <Tooltip title="Promote this Skill to a Topic">
                <span
                  onClick={() => openCreateTopicModalWithSkill(record._id)}
                  style={{
                    padding: "3px 8px",
                    borderRadius: "4px",
                    fontSize: "11px",
                    fontWeight: "500",
                    backgroundColor: "#fff",
                    color: "#595959",
                    border: "1px solid #d9d9d9",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    transition: "all 0.2s",
                    height: "24px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = "#52c41a";
                    e.currentTarget.style.backgroundColor = "#f6ffed";
                    e.currentTarget.style.borderColor = "#b7eb8f";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = "#595959";
                    e.currentTarget.style.backgroundColor = "#fff";
                    e.currentTarget.style.borderColor = "#d9d9d9";
                  }}
                >
                  + Promote
                </span>
              </Tooltip>
            )}

            <div style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
              <Tooltip title="Edit Skill">
                <span
                  onClick={() => openEditModal(record)}
                  style={{
                    width: "28px",
                    height: "28px",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "50%",
                    cursor: "pointer",
                    color: "#1890ff",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#e6f7ff"}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                >
                  <FaPen style={{ fontSize: "12px" }} />
                </span>
              </Tooltip>
              <Popconfirm
                title="Delete this skill?"
                okText="Delete"
                cancelText="Cancel"
                onConfirm={() => handleDelete(record)}
              >
                <Tooltip title="Delete Skill">
                  <span
                    style={{
                      width: "28px",
                      height: "28px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "50%",
                      cursor: "pointer",
                      color: "#ff4d4f",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#fff1f0"}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                  >
                    <FaTrash style={{ fontSize: "12px" }} />
                  </span>
                </Tooltip>
              </Popconfirm>
            </div>
          </div>
        );
      },
    },
  ];

  const topicColumns = [
    {
      title: "Topic",
      dataIndex: "name",
      key: "name",
      width: 200,
      render: (value, record) => (
        <div>
          <strong style={{ fontSize: 15, color: "#0f172a" }}>{value}</strong>
          {record.description ? (
            <Tooltip title={record.description}>
              <div style={{ 
                marginTop: 2, 
                fontSize: 12, 
                color: "#64748b",
                whiteSpace: 'nowrap', 
                overflow: 'hidden', 
                textOverflow: 'ellipsis', 
                maxWidth: '180px' 
              }}>
                {record.description}
              </div>
            </Tooltip>
          ) : (
            <div style={{ marginTop: 2, fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>
              No description
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Categories",
      dataIndex: "categories",
      key: "categories",
      width: 160,
      render: (cats) => (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
          {cats && cats.length > 0 ? (
            cats.map((c) => (
              <Tag key={c._id || c} color="cyan" style={{ margin: 0 }}>
                {c.name || c.title || c.code || 'Category'}
              </Tag>
            ))
          ) : (
            <span style={{ color: '#94a3b8', fontSize: 12 }}>No categories</span>
          )}
        </div>
      ),
    },
    {
      title: "Tags",
      key: "tags",
      width: 150,
      render: (_, record) => {
        const existingTags = record.tags || [];
        const trendingTag = availableTags.find(t => t.slug === 'trending');
        const popularTag = availableTags.find(t => t.slug === 'popular');
        
        const customTags = existingTags.filter(t => {
          const id = t._id || t;
          return id !== trendingTag?._id && id !== popularTag?._id;
        });

        return (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
            {customTags.length > 0 ? (
              customTags.map((tag) => {
                const tagObj = typeof tag === 'object' ? tag : availableTags.find(at => at._id === tag);
                return (
                  <Tag key={tagObj?._id || tag} color="blue" style={{ margin: 0 }}>
                    {tagObj?.name || tag}
                  </Tag>
                );
              })
            ) : (
              <span style={{ color: '#94a3b8', fontSize: 12, fontStyle: 'italic' }}>No tags</span>
            )}
          </div>
        );
      }
    },
    {
      title: "Traits",
      key: "traits",
      align: "center",
      width: 90,
      render: (_, record) => {
        const tags = record.tags || [];
        const trendingTag = availableTags.find(t => t.slug === 'trending');
        const popularTag = availableTags.find(t => t.slug === 'popular');

        const isTrending = tags.some(t => t === trendingTag?._id || t._id === trendingTag?._id);
        const isPopular = tags.some(t => t === popularTag?._id || t._id === popularTag?._id);
        return (
          <div className="d-flex align-items-center justify-content-center gap-1">
            {/* Popular Badge (Fire) */}
            <Tooltip title={isPopular ? "Remove from Popular" : "Mark as Popular"}>
              <button
                type="button"
                style={{ 
                  width: '30px',
                  height: '30px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.215, 0.610, 0.355, 1)',
                  backgroundColor: isPopular ? '#fff2e8' : '#f5f5f5',
                  border: isPopular ? '1px solid #ffd591' : '1px solid #e8e8e8',
                  color: isPopular ? '#fa8c16' : '#bfbfbf',
                  boxShadow: isPopular ? '0 2px 8px rgba(250, 140, 22, 0.15)' : 'none',
                  padding: 0,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.15)';
                  if (!isPopular) {
                    e.currentTarget.style.backgroundColor = '#fffbe6';
                    e.currentTarget.style.borderColor = '#ffe58f';
                    e.currentTarget.style.color = '#faad14';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  if (!isPopular) {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                    e.currentTarget.style.borderColor = '#e8e8e8';
                    e.currentTarget.style.color = '#bfbfbf';
                  }
                }}
                onClick={() => togglePopular(record)}
              >
                <FaFire style={{ fontSize: '15px' }} />
              </button>
            </Tooltip>

            {/* Trending Badge (ChartLine) */}
            <Tooltip title={isTrending ? "Remove from Trending" : "Mark as Trending"}>
              <button
                type="button"
                style={{ 
                  width: '30px',
                  height: '30px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.215, 0.610, 0.355, 1)',
                  backgroundColor: isTrending ? '#e6f7ff' : '#f5f5f5',
                  border: isTrending ? '1px solid #91d5ff' : '1px solid #e8e8e8',
                  color: isTrending ? '#1890ff' : '#bfbfbf',
                  boxShadow: isTrending ? '0 2px 8px rgba(24, 144, 255, 0.15)' : 'none',
                  padding: 0,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.15)';
                  if (!isTrending) {
                    e.currentTarget.style.backgroundColor = '#e6f7ff';
                    e.currentTarget.style.borderColor = '#91d5ff';
                    e.currentTarget.style.color = '#1890ff';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  if (!isTrending) {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                    e.currentTarget.style.borderColor = '#e8e8e8';
                    e.currentTarget.style.color = '#bfbfbf';
                  }
                }}
                onClick={() => toggleTrending(record)}
              >
                <FaChartLine style={{ fontSize: '15px' }} />
              </button>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: "Page Status",
      key: "pageStatus",
      width: 95,
      render: (_, record) => {
        const status = record.page?.status || "missing";
        return (
          <Tooltip title={PAGE_STATUS_DESCRIPTIONS[status]} placement="topLeft">
            <div style={{ cursor: "help", display: "inline-block" }}>
              <StatusPill status={status} />
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      width: 110,
      render: (_, record) => {
        const hasPage = record.page?._id && record.page?.status && record.page?.status !== "missing";
        return (
          <div style={{ display: "flex", gap: 6, justifyContent: "center", alignItems: "center" }}>
            {hasPage ? (
              <Tooltip title="Open Page Builder">
                <Button
                  type="primary"
                  shape="circle"
                  size="small"
                  onClick={() => openPageEditor(record)}
                  icon={<FaFileAlt style={{ fontSize: 11 }} />}
                  style={{
                    backgroundColor: "#722ed1",
                    borderColor: "#722ed1",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                />
              </Tooltip>
            ) : (
              <Tooltip title="Initialize Draft Page">
                <Button
                  type="dashed"
                  shape="circle"
                  size="small"
                  onClick={() => openPageEditor(record)}
                  icon={<FaFileAlt style={{ fontSize: 11, color: "#fa8c16" }} />}
                  style={{
                    borderColor: "#ffd591",
                    backgroundColor: "#fffbe6",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                />
              </Tooltip>
            )}
            <IconAction
              title="Edit topic settings"
              color="#722ed1"
              icon={<FaPen style={{ fontSize: 12 }} />}
              onClick={() => openEditTopicModal(record)}
            />
            <Popconfirm
              title="Remove from Topics?"
              description="This will not delete the skill, only unmark it as a topic."
              okText="Remove"
              cancelText="Cancel"
              onConfirm={() => handleRemoveTopic(record)}
            >
              <Button 
                type="text" 
                size="small" 
                shape="circle" 
                danger 
                icon={<FaTrash style={{ fontSize: 12 }} />} 
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              />
            </Popconfirm>
          </div>
        );
      },
    },
  ];


  // --- Skill CSV Handlers ---
  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const response = await axiosHttp.get("/v1/skills/export", {
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `skills_taxonomy_${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      message.success("CSV export downloaded successfully.");
    } catch (error) {
      message.error(getApiErrorMessage(error, "Failed to export CSV"));
    } finally {
      setExporting(false);
    }
  };

  const handleImportPreview = async () => {
    if (importFileList.length === 0) {
      message.error("Please select a CSV file first.");
      return;
    }
    const formData = new FormData();
    formData.append("file", importFileList[0]);

    setImportLoading(true);
    try {
      const response = await axiosHttp.post("/v1/skills/import", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      if (response.data && response.data.status) {
        setImportPreviewData(response.data.results);
        message.success("CSV analyzed successfully. Please review changes below.");
      } else {
        message.error(response.data?.message || "Failed to analyze CSV.");
      }
    } catch (error) {
      message.error(getApiErrorMessage(error, "Failed to analyze CSV."));
    } finally {
      setImportLoading(false);
    }
  };

  const handleImportConfirm = async () => {
    if (importFileList.length === 0) {
      message.error("Please select a CSV file first.");
      return;
    }
    const formData = new FormData();
    formData.append("file", importFileList[0]);

    setConfirmLoading(true);
    try {
      const response = await axiosHttp.post("/v1/skills/import?confirm=true", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      if (response.data && response.data.status) {
        if (importPreviewData && Array.isArray(importPreviewData.changes)) {
          const codes = new Set(importPreviewData.changes.map((c) => c.skillCode).filter(Boolean));
          setRecentlyUpdatedCodes(codes);
          setShowRecentlyUpdatedOnly(true);
        }
        message.success("Taxonomy updated successfully and cache cleared!");
        setIsImportModalOpen(false);
        setImportFileList([]);
        setImportPreviewData(null);
        fetchSkills(); // Refresh the main skills list
      } else {
        message.error(response.data?.message || "Failed to apply changes.");
      }
    } catch (error) {
      message.error(getApiErrorMessage(error, "Failed to apply changes."));
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleResetImport = () => {
    setImportFileList([]);
    setImportPreviewData(null);
  };

  const tabItems = [
    {
      key: "skills",
      label: "Skills",
      children: (
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4" style={{ gap: 16, flexWrap: "wrap" }}>
              <div className="d-flex align-items-center gap-3" style={{ flexWrap: "wrap", flex: 1 }}>
                <div style={{ width: '300px' }}>
                  <Input
                    placeholder="Search by name, code, or slug"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ borderRadius: '6px', borderColor: '#d9d9d9', height: '38px', margin: 0 }}
                  />
                </div>
                <Button
                  onClick={() => setExpandedRowKeys([])}
                  style={{ height: '38px', borderRadius: '6px', display: 'inline-flex', alignItems: 'center' }}
                >
                  Collapse All
                </Button>
                {recentlyUpdatedCodes.size > 0 && (
                  <Tag
                    closable
                    color="success"
                    onClose={() => {
                      setRecentlyUpdatedCodes(new Set());
                      setShowRecentlyUpdatedOnly(false);
                    }}
                    style={{ padding: '4px 10px', display: 'inline-flex', alignItems: 'center', gap: 6, height: '38px', borderRadius: '6px', margin: 0 }}
                  >
                    <Switch
                      size="small"
                      checked={showRecentlyUpdatedOnly}
                      onChange={(checked) => setShowRecentlyUpdatedOnly(checked)}
                    />
                    <span style={{ fontWeight: 600, color: '#1b5e20' }}>
                      Show recently imported ({recentlyUpdatedCodes.size})
                    </span>
                  </Tag>
                )}
              </div>
              <Space wrap>
                <Button 
                  icon={<FaDownload />} 
                  onClick={handleExportCsv} 
                  loading={exporting}
                  style={{ height: '38px', borderRadius: '6px', display: 'inline-flex', alignItems: 'center' }}
                >
                  Export CSV
                </Button>
                <Button 
                  icon={<FaUpload />} 
                  onClick={() => {
                    handleResetImport();
                    setIsImportModalOpen(true);
                  }}
                  style={{ height: '38px', borderRadius: '6px', display: 'inline-flex', alignItems: 'center' }}
                >
                  Bulk Import
                </Button>
                <Button 
                  type="primary" 
                  onClick={openCreateModal}
                  style={{ height: '38px', borderRadius: '6px' }}
                >
                  Add Skill
                </Button>
              </Space>
            </div>
             <Table
                rowKey={(record) => record._id}
                columns={skillColumns}
                dataSource={skillTableData}
                loading={loading}
                tableLayout="fixed"
                pagination={{ pageSize: 50 }}
                expandedRowKeys={expandedRowKeys}
                onExpandedRowsChange={(keys) => setExpandedRowKeys(keys)}
                rowClassName={(record) => recentlyUpdatedCodes.has(record.code) ? "row-recently-updated" : ""}
                onRow={(record) => ({
                  onDragOver: (e) => {
                    e.preventDefault();
                    if (dragOverSkillId !== record._id) {
                      setDragOverSkillId(record._id);
                    }
                  },
                  onDragLeave: () => {
                    if (dragOverSkillId === record._id) {
                      setDragOverSkillId(null);
                    }
                  },
                  onDrop: async (e) => {
                    e.preventDefault();
                    setDragOverSkillId(null);
                    const draggedSkillId = e.dataTransfer.getData("draggedSkillId");
                    if (draggedSkillId && draggedSkillId !== record._id) {
                      handleDragDropParentChange(draggedSkillId, record._id);
                    }
                  },
                  style: {
                    backgroundColor: dragOverSkillId === record._id ? "#e6f7ff" : undefined,
                    border: dragOverSkillId === record._id ? "1px dashed #1890ff" : undefined,
                  }
                })}
              />
          </div>
        </div>
      )
    },
    {
      key: "topics",
      label: "Topics",
      children: (
        <div>
          <div className="row mb-3">
            <div className="col-md-3 mb-2"><StatCard label="Total Topics" value={topicStats.total} /></div>
            <div className="col-md-3 mb-2"><StatCard label="Pages Initialized" value={topicStats.initialized} tone="#722ed1" /></div>
            <div className="col-md-3 mb-2"><StatCard label="Popular Topics" value={topics.filter(t => (t.tags || []).includes('popular')).length} tone="#fa8c16" /></div>
            <div className="col-md-3 mb-2"><StatCard label="Trending Topics" value={topics.filter(t => (t.tags || []).includes('trending')).length} tone="#1890ff" /></div>
          </div>

          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start mb-3" style={{ gap: 16, flexWrap: "wrap" }}>
                <div>
                  <h4 style={{ margin: 0, fontWeight: 800, color: "#0f172a" }}>User-facing Topics</h4>
                  <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
                    Promote skills into public topic pages, assign categories, and control popular or trending traits.
                  </div>
                </div>
                <Space wrap align="center">
                  <Select
                    value={topicStatusFilter}
                    onChange={setTopicStatusFilter}
                    style={{ width: 170, height: '38px' }}
                    options={[
                      { label: "All Topics", value: "all" },
                      { label: "Pages Initialized", value: "initialized" },
                      { label: "Missing Pages", value: "missing" },
                    ]}
                  />
                  <div style={{ width: 320 }}>
                    <Input
                      placeholder="Search topics..."
                      value={topicSearchText}
                      onChange={(e) => setTopicSearchText(e.target.value)}
                      style={{ borderRadius: '6px', borderColor: '#d9d9d9', height: '38px', margin: 0 }}
                    />
                  </div>
                </Space>
              </div>
              <div style={{ width: "100%", overflowX: "auto" }}>
                <Table
                  rowKey={(record) => record._id}
                  columns={topicColumns}
                  dataSource={topicTableData}
                  loading={loadingTopics}
                  size="middle"
                  tableLayout="fixed"
                  scroll={{ x: "max-content" }}
                  pagination={{ pageSize: 50 }}
                />
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <Page title="Skills & Topics">
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="mb-3">
            <h3 className="mb-0">Skills & Topics Administration</h3>
          </div>

          <Tabs 
            activeKey={activeTab} 
            onChange={handleTabChange} 
            items={tabItems}
            type="card"
          />

          {/* Skill Modal */}
          <Modal
            open={isModalOpen}
            onCancel={() => setIsModalOpen(false)}
            onOk={handleSubmit}
            okText={editingSkill ? "Update" : "Create"}
            confirmLoading={saving}
            title={editingSkill ? "Edit Skill" : "Add Skill"}
          >
            <Form layout="vertical" form={form}>
              <Form.Item
                label="Parent Skill"
                name="parent"
                extra="Leave blank to create a top-level macro skill."
              >
                <Select
                  placeholder="Select a Parent (Optional)"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={parentOptions}
                />
              </Form.Item>
              <Form.Item
                label="Name"
                name="name"
                rules={[{ required: true, message: "Skill name is required" }]}
              >
                <Input placeholder="e.g. MongoDB Aggregation" />
              </Form.Item>
              <Form.Item
                label="Code"
                name="code"
                rules={[{ required: true, message: "Skill code is required" }]}
              >
                <Input placeholder="e.g. mongodb_aggregation" />
              </Form.Item>
              <Form.Item label="Slug" name="slug">
                <Input placeholder="e.g. mongodb-aggregation" />
              </Form.Item>
            </Form>
          </Modal>

          {/* Topic Modal */}
          <Modal
            open={isTopicModalOpen}
            onCancel={() => setIsTopicModalOpen(false)}
            onOk={handleTopicSubmit}
            okText={editingTopic ? "Update Topic" : "Add Topic"}
            confirmLoading={savingTopic}
            title={editingTopic ? "Edit Topic Settings" : "Add Topic"}
          >
            <Form layout="vertical" form={topicForm}>
              <Form.Item
                label="Skill"
                name="skill_id"
                rules={[{ required: true, message: "Please select a skill" }]}
                extra={editingTopic ? "Skill cannot be changed while editing." : "Search and select a skill to promote to a topic."}
              >
                <Select
                  placeholder="Select a Skill"
                  showSearch
                  optionFilterProp="label"
                  options={allSkillsOptions}
                  disabled={!!editingTopic}
                />
              </Form.Item>
              
              <Form.Item
                label="Categories"
                name="categories"
                extra="Assign this topic to one or more categories."
              >
                <Select
                  mode="multiple"
                  placeholder="Select Categories"
                  showSearch
                  optionFilterProp="label"
                  options={categoryOptions}
                />
              </Form.Item>

              <Form.Item
                label="Description"
                name="description"
                extra="Short description of this topic for public pages."
              >
                <Input.TextArea rows={3} placeholder="Enter description..." />
              </Form.Item>

              <Form.Item
                label="Topic Tags (Max 3 Tags)"
                name="custom_tags"
                extra="Select up to 3 tags from all skill-scoped tags. Type to create a new tag."
              >
                <Select
                  mode="tags"
                  maxCount={3}
                  placeholder="Select or type up to 3 tags..."
                  style={{ width: '100%' }}
                  showSearch
                  optionFilterProp="label"
                  tokenSeparators={[',']}
                  options={availableTags.filter(t => t.slug !== 'trending' && t.slug !== 'popular').map(t => ({
                    label: (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        {renderTagIcon(t)}
                        <span>{t.name}</span>
                      </span>
                    ),
                    value: t._id,
                    searchValue: t.name
                  }))}
                />
              </Form.Item>

              <div className="row">
                <div className="col-md-6">
                  <Form.Item
                    label="Popular"
                    name="is_popular"
                    valuePropName="checked"
                    extra="Mark this topic as popular."
                  >
                    <Switch checkedChildren={<FaFire />} unCheckedChildren={<FaFire />} />
                  </Form.Item>
                </div>
                <div className="col-md-6">
                  <Form.Item
                    label="Trending"
                    name="is_trending"
                    valuePropName="checked"
                    extra="Mark this topic as trending."
                  >
                    <Switch checkedChildren={<FaChartLine />} unCheckedChildren={<FaChartLine />} />
                  </Form.Item>
                </div>
              </div>
            </Form>
          </Modal>

          {/* Import CSV Modal */}
          <Modal
            title={
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600 }}>
                <FaUpload style={{ color: '#1890ff' }} />
                Bulk Import
              </span>
            }
            open={isImportModalOpen}
            onCancel={() => {
              if (!importLoading && !confirmLoading) {
                setIsImportModalOpen(false);
              }
            }}
            footer={null}
            width={800}
            destroyOnClose
            style={{ top: 40 }}
          >
            {!importPreviewData ? (
              <div style={{ padding: '8px 0' }}>
                <div style={{ marginBottom: 16 }}>
                  <Alert
                    message="Import Guidelines"
                    description={
                      <ul style={{ paddingLeft: 16, margin: '4px 0 0 0' }}>
                        <li>The CSV file must match skills by their unique <strong>code</strong>.</li>
                        <li>To change a skill's parent, update the <strong>parent_code</strong> column.</li>
                        <li>Set <strong>parent_code</strong> to blank to move a skill to the root level.</li>
                        <li>Self-parenting and cyclic references (loops) will be automatically detected and blocked.</li>
                      </ul>
                    }
                    type="info"
                    showIcon
                  />
                </div>

                <Upload.Dragger
                  accept=".csv"
                  fileList={importFileList}
                  beforeUpload={(file) => {
                    const isCsv = file.name.endsWith(".csv") || file.type === "text/csv";
                    if (!isCsv) {
                      message.error(`${file.name} is not a CSV file.`);
                      return Upload.LIST_IGNORE;
                    }
                    setImportFileList([file]);
                    return false;
                  }}
                  onRemove={() => setImportFileList([])}
                  maxCount={1}
                >
                  <p className="ant-upload-drag-icon" style={{ fontSize: 40, color: '#1890ff' }}>
                    <FaFileAlt style={{ margin: '0 auto' }} />
                  </p>
                  <p className="ant-upload-text" style={{ fontSize: 16, fontWeight: 500 }}>
                    Click or drag CSV file to this area to upload
                  </p>
                  <p className="ant-upload-hint">
                    Select a CSV file containing 'code' and 'parent_code' columns.
                  </p>
                </Upload.Dragger>

                <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                  <Button 
                    onClick={() => setIsImportModalOpen(false)}
                    disabled={importLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    disabled={importFileList.length === 0}
                    loading={importLoading}
                    onClick={handleImportPreview}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    <FaSync />
                    Analyze CSV & Preview Changes
                  </Button>
                </div>
              </div>
            ) : (
              <div style={{ padding: '8px 0' }}>
                {/* Stats cards */}
                <div className="row mb-3">
                  <div className="col-md-4 mb-2">
                    <Card size="small" style={{ borderRadius: 8, borderColor: "#edf1f7", textAlign: 'center' }}>
                      <div style={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>TOTAL SKILLS</div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: "#0f172a" }}>{importPreviewData.totalSkills}</div>
                    </Card>
                  </div>
                  <div className="col-md-4 mb-2">
                    <Card size="small" style={{ borderRadius: 8, borderColor: "#edf1f7", textAlign: 'center' }}>
                      <div style={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>UNCHANGED</div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: "#16a34a" }}>{importPreviewData.unchangedCount}</div>
                    </Card>
                  </div>
                  <div className="col-md-4 mb-2">
                    <Card size="small" style={{ borderRadius: 8, borderColor: "#edf1f7", textAlign: 'center' }}>
                      <div style={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>PROPOSED CHANGES</div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: importPreviewData.changes.length > 0 ? "#ca8a04" : "#64748b" }}>
                        {importPreviewData.changes.length}
                      </div>
                    </Card>
                  </div>
                </div>

                {/* Status messages */}
                {importPreviewData.errors.length > 0 && (
                  <Alert
                    message={`${importPreviewData.errors.length} validation errors found. These updates will be skipped.`}
                    type="error"
                    showIcon
                    style={{ marginBottom: 12 }}
                  />
                )}
                {importPreviewData.warnings.length > 0 && (
                  <Alert
                    message={`${importPreviewData.warnings.length} warnings found. These updates will be skipped.`}
                    type="warning"
                    showIcon
                    style={{ marginBottom: 12 }}
                  />
                )}
                {importPreviewData.changes.length === 0 && (
                  <Alert
                    message="No reparenting updates found. The CSV matches the current database structure."
                    type="success"
                    showIcon
                    style={{ marginBottom: 12 }}
                  />
                )}

                <Tabs defaultActiveKey="changes" type="line" style={{ marginTop: 12 }}>
                  <Tabs.TabPane 
                    tab={`Proposed Changes (${importPreviewData.changes.length})`} 
                    key="changes"
                  >
                    <Table
                      dataSource={importPreviewData.changes}
                      rowKey={(record, index) => index}
                      size="small"
                      pagination={{ pageSize: 5 }}
                      columns={[
                        {
                          title: "Skill",
                          dataIndex: "skillName",
                          key: "skillName",
                          render: (text, record) => (
                            <div>
                              <span style={{ fontWeight: 600 }}>{text}</span>
                              <div style={{ fontSize: 11, color: '#64748b' }}>{record.skillCode}</div>
                            </div>
                          )
                        },
                        {
                          title: "Current Parent",
                          dataIndex: "currentLabel",
                          key: "currentLabel",
                          render: (text, record) => (
                            <Tag color="default">{text || "(root)"}</Tag>
                          )
                        },
                        {
                          title: "New Parent",
                          dataIndex: "newLabel",
                          key: "newLabel",
                          render: (text, record) => (
                            <Tag color="blue">{text || "(root)"}</Tag>
                          )
                        }
                      ]}
                    />
                  </Tabs.TabPane>

                  {importPreviewData.errors.length > 0 && (
                    <Tabs.TabPane 
                      tab={
                        <span style={{ color: '#ff4d4f' }}>
                          <FaTimesCircle style={{ marginRight: 6, verticalAlign: 'middle' }} />
                          Errors ({importPreviewData.errors.length})
                        </span>
                      } 
                      key="errors"
                    >
                      <div style={{ maxHeight: 200, overflowY: 'auto', padding: '8px 12px', border: '1px solid #f0f0f0', borderRadius: 6 }}>
                        {importPreviewData.errors.map((err, idx) => (
                          <div key={idx} style={{ padding: '6px 0', borderBottom: idx < importPreviewData.errors.length - 1 ? '1px solid #f0f0f0' : 'none', color: '#ff4d4f', fontSize: 13, display: 'flex', gap: 8 }}>
                            <span>•</span> <span>{err}</span>
                          </div>
                        ))}
                      </div>
                    </Tabs.TabPane>
                  )}

                  {importPreviewData.warnings.length > 0 && (
                    <Tabs.TabPane 
                      tab={
                        <span style={{ color: '#faad14' }}>
                          <FaExclamationTriangle style={{ marginRight: 6, verticalAlign: 'middle' }} />
                          Warnings ({importPreviewData.warnings.length})
                        </span>
                      } 
                      key="warnings"
                    >
                      <div style={{ maxHeight: 200, overflowY: 'auto', padding: '8px 12px', border: '1px solid #f0f0f0', borderRadius: 6 }}>
                        {importPreviewData.warnings.map((warn, idx) => (
                          <div key={idx} style={{ padding: '6px 0', borderBottom: idx < importPreviewData.warnings.length - 1 ? '1px solid #f0f0f0' : 'none', color: '#faad14', fontSize: 13, display: 'flex', gap: 8 }}>
                            <span>•</span> <span>{warn}</span>
                          </div>
                        ))}
                      </div>
                    </Tabs.TabPane>
                  )}
                </Tabs>

                <div style={{ marginTop: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Button 
                    onClick={handleResetImport}
                    disabled={confirmLoading}
                    icon={<FaSync />}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    Upload Different File
                  </Button>

                  <Space>
                    <Button 
                      onClick={() => setIsImportModalOpen(false)}
                      disabled={confirmLoading}
                    >
                      Close
                    </Button>
                    <Button
                      type="primary"
                      disabled={importPreviewData.changes.length === 0}
                      loading={confirmLoading}
                      onClick={handleImportConfirm}
                      style={{ 
                        backgroundColor: importPreviewData.changes.length > 0 ? '#52c41a' : undefined,
                        borderColor: importPreviewData.changes.length > 0 ? '#52c41a' : undefined,
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        gap: 6 
                      }}
                    >
                      <FaCheckCircle />
                      Confirm & Apply {importPreviewData.changes.length} Changes
                    </Button>
                  </Space>
                </div>
              </div>
            )}
          </Modal>

        </div>
      </div>
    </Page>
  );
};

export default SkillList;
